/** The Add / edit model form state, and the Test-before-Save lock. */

import { useMemo, useState } from 'react';

import { useSaveModel, useTestDraftModel } from '@/api/queries';
import type { AiModel, AiModelInput, Capability, Hosting } from '@/api/types';
import { serverFieldErrors } from '@/api/validate';

import { authMethodsFor, formErrors, guessVendor, hostingDefaults, initialForm, MIN_OUTPUT_TOKENS } from './modelForm';

/** Save unlocks only for the exact, valid values that passed Test; any edit locks it again. */
function testLock(result: { ok: boolean } | undefined, tested: string | null, fingerprint: string, invalid: boolean) {
  return { passed: result?.ok === true && tested === fingerprint && !invalid, stale: !!result && tested !== fingerprint };
}

export function useModelForm(hostings: Hosting[], model: AiModel | undefined, onClose: () => void) {
  const save = useSaveModel();
  const test = useTestDraftModel();
  const [testedFingerprint, setTestedFingerprint] = useState<string | null>(null);
  const firstHosting = hostings[0] as Hosting;
  const [form, setForm] = useState<AiModelInput>(() => initialForm(model, firstHosting));

  const hosting = hostings.find((h) => h.id === form.hosting) ?? firstHosting;
  const vendor = guessVendor(form.hosting, form.model_id, form.config);
  const authMethods = useMemo(() => authMethodsFor(hosting, vendor), [hosting, vendor]);
  const auth = authMethods.find((a) => a.id === form.auth) ?? authMethods[0];
  const fields = [...hosting.fields, ...(auth?.fields ?? [])];
  // Stored credentials survive only while the auth method stays the same.
  const isStored = (key: string) => !!model && model.auth === auth?.id && model.stored_secrets.includes(key);

  const set = <K extends keyof AiModelInput>(key: K, value: AiModelInput[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setConfig = (key: string, value: string) => setForm((f) => ({ ...f, config: { ...f.config, [key]: value } }));

  function changeCapability(capability: Capability) {
    // An embedding model has no output tokens to cap, but the API still wants a valid limit.
    setForm((f) => ({ ...f, capability, max_output_tokens: capability === 'embedding' && f.max_output_tokens === '' ? MIN_OUTPUT_TOKENS : f.max_output_tokens }));
  }

  function changeHosting(id: string) {
    const next = hostings.find((h) => h.id === id);
    if (!next) return;
    // A hosting change resets auth and provider settings to that hosting's defaults.
    setForm((f) => ({ ...f, ...hostingDefaults(next) }));
  }

  const body = { ...form, auth: auth?.id ?? form.auth };
  const fingerprint = JSON.stringify(body);
  const invalid = Object.keys(formErrors(form)).length > 0;
  const { passed, stale } = testLock(test.data, testedFingerprint, fingerprint, invalid);
  const server = serverFieldErrors(save.error ?? test.error);

  function runTest(button: HTMLButtonElement) {
    if (!button.form?.reportValidity() || invalid) return;
    setTestedFingerprint(fingerprint);
    test.mutate(model ? { ...body, key: model.key } : body);
  }

  function submit() {
    if (!passed) return;
    if (model) {
      const { hosting: _hosting, ...patch } = body; // hosting is fixed once created
      save.mutate({ key: model.key, body: patch }, { onSuccess: onClose });
    } else {
      save.mutate({ body }, { onSuccess: onClose });
    }
  }

  return { save, test, form, hosting, authMethods, auth, fields, isStored, set, setConfig, changeHosting, changeCapability, passed, stale, server, runTest, submit };
}

export type ModelFormState = ReturnType<typeof useModelForm>;
